import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchUser } from '../store/slices/userSlice';
import { startConversation } from '../store/slices/messagesSlice';
import { logout } from '../store/slices/authSlice';
import { usersAPI, uploadAPI } from '../services/api';
import PostCard from '../components/PostCard';
import NewsInterestSelector from '../components/NewsInterestSelector';
import { slugToLabel } from '../constants/newsInterests';
import './Profile.css';

const Profile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { profile, loading, error } = useAppSelector((state) => state.user);
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [posts, setPosts] = useState<any[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [followStats, setFollowStats] = useState({ followers: 0, following: 0 });
  const [isFollowing, setIsFollowing] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [trustVote, setTrustVote] = useState(0);
  const [voteCount, setVoteCount] = useState(0);
  const [hoverStar, setHoverStar] = useState(0);
  const [editingInterests, setEditingInterests] = useState(false);
  const [interestsDraft, setInterestsDraft] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (id) {
      dispatch(fetchUser(id));
      loadUserPosts(id);
      loadFollowStats(id);
      loadTrustVote(id);
    }
  }, [dispatch, id]);

  const loadTrustVote = async (userId: string) => {
    try {
      const res = await usersAPI.getTrustVote(userId);
      setTrustVote(res.data.rating);
      setVoteCount(res.data.vote_count);
    } catch (err) {
      console.error('Failed to load trust vote:', err);
    }
  };

  const handleRateTrust = async (rating: number) => {
    if (!id) return;
    const prevVote = trustVote;
    setTrustVote(rating);
    try {
      await usersAPI.rateTrust(id, rating);
      dispatch(fetchUser(id));
      loadTrustVote(id);
    } catch (err) {
      console.error('Failed to rate trust:', err);
      setTrustVote(prevVote);
    }
  };

  const loadUserPosts = async (userId: string) => {
    setPostsLoading(true);
    try {
      const response = await usersAPI.getUserPosts(userId);
      setPosts(response.data.posts || []);
    } catch (err) {
      console.error('Failed to load posts:', err);
    }
    setPostsLoading(false);
  };

  const loadFollowStats = async (userId: string) => {
    try {
      const [followersRes, followingRes] = await Promise.all([
        usersAPI.getFollowers(userId, 1, 1),
        usersAPI.getFollowing(userId, 1, 1),
      ]);
      setFollowStats({
        followers: followersRes.data.total,
        following: followingRes.data.total,
      });
    } catch (err) {
      console.error('Failed to load follow stats:', err);
    }
  };

  const handleFollow = async () => {
    if (!id) return;
    try {
      if (isFollowing) {
        await usersAPI.unfollow(id);
        setIsFollowing(false);
        setFollowStats((prev) => ({ ...prev, followers: prev.followers - 1 }));
      } else {
        await usersAPI.follow(id);
        setIsFollowing(true);
        setFollowStats((prev) => ({ ...prev, followers: prev.followers + 1 }));
      }
    } catch (err) {
      console.error('Failed to follow/unfollow:', err);
    }
  };

  const handleMessage = async () => {
    if (!id) return;
    try {
      const conv = await dispatch(startConversation(id)).unwrap();
      navigate(`/messages/${conv.id}`);
    } catch (err) {
      console.error('Failed to start conversation:', err);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    setUploadingAvatar(true);
    try {
      const uploadRes = await uploadAPI.uploadImage(file);
      const avatarUrl = uploadRes.data.image_url;
      await usersAPI.updateUser(currentUser.id, { avatar_url: avatarUrl });
      // Refresh profile to show new avatar
      dispatch(fetchUser(currentUser.id));
      // Update localStorage so other components reflect the change
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      stored.avatar_url = avatarUrl;
      localStorage.setItem('user', JSON.stringify(stored));
    } catch (err) {
      console.error('Failed to update avatar:', err);
    }
    setUploadingAvatar(false);
    // Reset input so the same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getTrustClass = (rating: number) => {
    if (rating >= 7) return 'trust-high';
    if (rating >= 4) return 'trust-medium';
    return 'trust-low';
  };

  if (loading) {
    return (
      <div className="profile">
        <div className="loading-state">Loading profile...</div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="profile">
        <div className="error-state">
          <p>User not found</p>
          <Link to="/">Go back home</Link>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser?.id === profile.id;

  return (
    <div className="profile">
      <header className="profile-header">
        <Link to="/" className="back-btn">← Back</Link>
        <div>
          <h2>{profile.display_name || profile.username}</h2>
          <span className="post-count">{posts.length} posts</span>
        </div>
      </header>

      <div className="profile-banner" />

      <div className="profile-info">
        <div className="profile-avatar-section">
          <div className="profile-avatar-wrapper">
            <img
              src={profile.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.username}`}
              alt={profile.display_name}
              className="profile-avatar"
            />
            {isOwnProfile && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleAvatarChange}
                />
                <button
                  className="avatar-change-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  title="Change profile picture"
                >
                  {uploadingAvatar ? '...' : '📷'}
                </button>
              </>
            )}
          </div>
          {isOwnProfile ? (
            <div className="profile-actions">
              <button className="profile-logout-btn" onClick={() => { dispatch(logout()); navigate('/login'); }}>
                Logout
              </button>
            </div>
          ) : (
            <div className="profile-actions">
              <button className="message-btn" onClick={handleMessage}>Message</button>
              <button
                className={`follow-btn ${isFollowing ? 'following' : ''}`}
                onClick={handleFollow}
              >
                {isFollowing ? 'Following' : 'Follow'}
              </button>
            </div>
          )}
        </div>

        <div className="profile-details">
          <h2 className="display-name">{profile.display_name || profile.username}</h2>
          <span className="username">@{profile.username}</span>
          {profile.is_admin && <span className="admin-badge">Admin</span>}
          {profile.country && <span className="country">{profile.country}</span>}

          <div className="trust-section">
            <span className={`trust-badge large ${getTrustClass(profile.trust_rating)}`}>
              ★ Trust Rating: {profile.trust_rating}/10
            </span>
            {!isOwnProfile && (
              <div className="trust-vote-row">
                <div className="trust-stars" onMouseLeave={() => setHoverStar(0)}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((star) => (
                    <span
                      key={star}
                      className={`trust-star ${star <= (hoverStar || trustVote) ? 'active' : ''}`}
                      onClick={() => handleRateTrust(star)}
                      onMouseEnter={() => setHoverStar(star)}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <span className="vote-count">{voteCount} vote{voteCount !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>

          {profile.bio && <p className="bio">{profile.bio}</p>}

          {profile.news_interests && profile.news_interests.length > 0 && !editingInterests && (
            <div className="interest-chips">
              {profile.news_interests.map((slug: string) => (
                <span key={slug} className="interest-chip">{slugToLabel(slug)}</span>
              ))}
            </div>
          )}
          {isOwnProfile && !editingInterests && (
            <button
              className="edit-interests-btn"
              onClick={() => {
                setInterestsDraft(profile.news_interests || []);
                setEditingInterests(true);
              }}
            >
              {profile.news_interests?.length ? 'Edit Interests' : 'Add News Interests'}
            </button>
          )}
          {editingInterests && (
            <div className="interests-editor">
              <NewsInterestSelector selectedInterests={interestsDraft} onChange={setInterestsDraft} />
              <div className="interests-editor-actions">
                <button
                  className="btn-primary"
                  onClick={async () => {
                    if (!currentUser) return;
                    try {
                      await usersAPI.updateUser(currentUser.id, { news_interests: interestsDraft });
                      dispatch(fetchUser(currentUser.id));
                      setEditingInterests(false);
                    } catch (err) {
                      console.error('Failed to update interests:', err);
                    }
                  }}
                >
                  Save
                </button>
                <button className="btn-secondary" onClick={() => setEditingInterests(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="stats">
            <span><strong>{followStats.following}</strong> Following</span>
            <span><strong>{followStats.followers}</strong> Followers</span>
          </div>
        </div>
      </div>

      <div className="profile-tabs">
        <button className="tab active">Posts</button>
        <button className="tab">Likes</button>
      </div>

      <div className="profile-posts">
        {postsLoading && <div className="loading-state">Loading posts...</div>}
        
        {!postsLoading && posts.length === 0 && (
          <div className="empty-state">
            <p>No posts yet</p>
          </div>
        )}

        {posts.map((post) => (
          <PostCard key={post.id} post={post} />
        ))}
      </div>
    </div>
  );
};

export default Profile;
